/**
 * FeaturedMaterials Component
 * ===========================
 * Materials carousel with filters
 * Content labels from Blueprint
 * Materials data from Supabase API
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint, BlueprintSection } from '../../context/BlueprintContext';
import { getMaterials } from '../../services/materialsApi';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const FeaturedMaterials = () => {
  const { homepage, getState, getRoute, buildRoute } = useBlueprint();
  const materials = homepage?.materials || {};
  const filters = materials?.filters || [];
  
  // Get empty state text from Blueprint
  const noMaterialsText = getState('no_materials', 'Nessun materiale trovato');
  
  // Get routes from config
  const explorerRoute = materials.seeAllLink || getRoute('explorer', '/materials');
  
  const [activeFilter, setActiveFilter] = useState('tutti');
  const [materialsList, setMaterialsList] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const carouselRef = useRef(null);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(true);
  
  // Fetch materials from API
  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      try {
        const result = await getMaterials({ limit: 20 });
        if (result.success) {
          setMaterialsList(result.data);
        }
      } catch (err) {
        console.error('Error fetching materials:', err);
      }
      setLoading(false);
    };
    
    fetchMaterials();
  }, []);
  
  // Filter materials by category
  const filteredMaterials = activeFilter === 'tutti'
    ? materialsList
    : materialsList.filter(m => 
        m.category?.toLowerCase().includes(activeFilter.toLowerCase())
      );
  
  // Carousel scroll handling
  const updateButtons = () => {
    if (!carouselRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < scrollWidth - clientWidth - 4);
  };
  
  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', updateButtons);
      updateButtons();
    }
    return () => carousel?.removeEventListener('scroll', updateButtons);
  }, [filteredMaterials]);
  
  const scroll = (direction) => {
    if (!carouselRef.current) return;
    const cardWidth = carouselRef.current.querySelector('.material-card')?.offsetWidth || 220;
    carouselRef.current.scrollBy({
      left: direction === 'prev' ? -(cardWidth + 16) : (cardWidth + 16),
      behavior: 'smooth'
    });
  };
  
  // Parse title with newlines
  const renderTitle = () => {
    const title = materials.title || '';
    return title.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < title.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };
  
  return (
    <BlueprintSection name="materials">
      <section className="bg-[var(--cream)] py-20 lg:py-24">
        <div className="sr-container">
          {/* Header */}
          <div className="flex items-end justify-between mb-9 gap-6 flex-wrap">
            <div>
              <p className="sr-label mb-4">
                {materials.label}
              </p>
              <h2 className="font-[var(--ff-h)] text-[clamp(32px,3.5vw,52px)] font-semibold text-[var(--dark)] leading-[1.1] tracking-[-0.4px] max-w-[520px]">
                {renderTitle()}
              </h2>
            </div>
            <Link
              to={explorerRoute}
              className="text-[13px] font-medium text-[#333] inline-flex items-center gap-1.5 hover:gap-2.5 transition-all"
            >
              {materials.seeAll}
              <ChevronRight size={15} />
            </Link>
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 mb-8 flex-wrap">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`sr-filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
              >
                {filter.label}
              </button>
            ))}
            <Link
              to={explorerRoute}
              className="sr-filter-btn gold"
            >
              {materials.seeAll} →
            </Link>
          </div>
          
          {/* Carousel */}
          <div className="relative">
            {/* Prev Button */}
            {showPrev && (
              <button
                onClick={() => scroll('prev')}
                className="absolute left-[-20px] top-[calc(50%-26px)] w-10 h-10 rounded-full bg-white border border-[#DDD9D2] flex items-center justify-center z-10 shadow-[0_4px_14px_rgba(0,0,0,0.12)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.18)] transition-shadow"
              >
                <ChevronLeft size={18} className="text-[#1A1A1A]" />
              </button>
            )}
            
            {/* Materials */}
            <div
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto scroll-snap-x-mandatory scroll-smooth pb-1 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {loading ? (
                // Loading skeletons
                Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="material-card min-w-[calc(100%/6-13.5px)] max-w-[calc(100%/6-13.5px)] flex-shrink-0 scroll-snap-start"
                  >
                    <div className="rounded-lg overflow-hidden aspect-[4/3] mb-3 bg-[#E8E4DE] animate-pulse" />
                    <div className="h-4 bg-[#E8E4DE] rounded animate-pulse mb-2 w-3/4" />
                    <div className="h-3 bg-[#E8E4DE] rounded animate-pulse w-1/2" />
                  </div>
                ))
              ) : filteredMaterials.length > 0 ? (
                filteredMaterials.map((material) => (
                  <Link
                    key={material.id}
                    to={buildRoute('material_detail', { slug: material.id })}
                    className="material-card min-w-[calc(100%/6-13.5px)] max-w-[calc(100%/6-13.5px)] flex-shrink-0 scroll-snap-start cursor-pointer group"
                  >
                    <div className="rounded-lg overflow-hidden aspect-[4/3] mb-3 shadow-[0_2px_12px_rgba(0,0,0,0.1)] transition-all duration-300 group-hover:scale-[1.04] group-hover:shadow-[0_10px_28px_rgba(0,0,0,0.18)]">
                      <img
                        src={material.image_url || 'https://images.unsplash.com/photo-1558346648-9757f2fa4474?w=500&q=80'}
                        alt={material.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="text-[13.5px] font-bold text-[var(--dark)] mb-1">
                      {material.name}
                    </div>
                    <div className="text-xs text-[#666] font-medium">
                      {material.category || 'Stone'}
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-[#666] py-8 w-full text-center">
                  {noMaterialsText}
                </div>
              )}
            </div>
            
            {/* Next Button */}
            {showNext && (
              <button
                onClick={() => scroll('next')}
                className="absolute right-[-20px] top-[calc(50%-26px)] w-10 h-10 rounded-full bg-white border border-[#DDD9D2] flex items-center justify-center z-10 shadow-[0_4px_14px_rgba(0,0,0,0.12)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.18)] transition-shadow"
              >
                <ChevronRight size={18} className="text-[#1A1A1A]" />
              </button>
            )}
          </div>
        </div>
      </section>
    </BlueprintSection>
  );
};

export default FeaturedMaterials;
